'use client';

import { useMemo } from 'react';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { LoginData, LoginRequestData, LoginResponse, useLogin } from '@/api/useLogin';
import { useStoreBoot } from '@/api/useStoreBoot';
import { useTrackPublisherEvents } from '@/api/useTrackPublisherEvents';
import {
  EEventsType,
  ELocalStorageKeys,
  ELoginMethods,
  ELoginMode,
  ELoginResultReason,
  EPublisherEvents,
  EQueryKeys,
  EQueryParams,
  EResultOptions,
  ESessionStorageKeys,
  ESource,
  EStorageType,
} from '@/lib/enums';
import {
  EPortalRumEvents,
  logPortalRumError,
  logPortalRumInfo,
  setPortalPlayerRumContext,
} from '@/lib/coralogix';
import { enrichOptions, getLoginEventsData, getSessionMetadataSource } from '@/lib/eventDataUtils';
import { resetMixpanelSession, trackMixpanelEvent } from '@/lib/mixpanelClient';
import { getLeafSlug, getRedirectPath } from '@/lib/pageRedirectUtils';
import { BootData } from '@/lib/types';
import { getPlatformData } from '@/lib/utils';
import {
  EPortalJourneyFlow,
  completePersistentPortalJourney,
  createPortalJourney,
  failPersistentPortalJourney,
} from '@/lib/portalJourney';
import { type IServerOtpLogin, toLoginRequestError } from '@/lib/serverOtpLogin';
import { useFacebook, useGoogle } from '@/hooks/useSocialProfile';
import { getEnvVariable } from '@/lib/getEnvVariable';
import { useQueryClient } from '@tanstack/react-query';
import { broadcastRegistry } from '@web-store-platforms/providers/broadcast-channel';

const STORE_REFRESH_CHANNEL_NAME = 'store-refresh';

import { loginPageService } from '../login-page.service';
import { loginStateService } from '../login.state.service';
import {
  EOtpAuthReasonCode,
  classifyLoginFailureReason,
  classifyOtpBusinessFailureReason,
  clearOtpUrlAuth,
  isTransportLoginFailure,
  OtpAuthReasonCode,
} from '@/lib/otpAuth';
import { readLandingJourneyId } from '@/lib/landingJourney';
import { useObservable } from '../observable';
import { webStorageUtil } from '../webStorage.state.service';
import { popupStateService } from '../popup.state.service';
import { dailyBonusStateService } from '../dailyBonus.state.service';
import { appStateService } from '../app.state.service';
import { useAppState } from './app.state.hook';
import { useIndexedDB } from './useIndexedDB';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

export const DEFAULT_TTL = 3600000;

export const useLoginState = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isConsentChecked = useObservable(loginStateService.isConsentChecked);
  const isOtpLoggingIn = Boolean(useObservable(loginStateService.isOtpLoggingIn));
  const t = useTranslations();

  const { getBoot } = useStoreBoot();

  const { initialBoot, setDisplayLoader, platform } = useAppState();
  const queryClient = useQueryClient();

  const bootData = getBoot.data as BootData;

  const facebook = useFacebook(bootData?.integration?.playersAuthentication?.fbAppId);
  const google = useGoogle();
  const { login } = useLogin();
  const { addValue, deleteAll } = useIndexedDB(['sessions']);

  const localAddress = searchParams.get(EQueryParams.LOCAL_ADDRESS);

  const shouldDisableLoginButtons = useMemo(() => {
    const consentRequired = initialBoot?.theme?.loginPopup?.consentSection?.consentRequired;

    return (consentRequired && !isConsentChecked) || isOtpLoggingIn;
  }, [isConsentChecked, isOtpLoggingIn, initialBoot?.theme?.loginPopup?.consentSection?.consentRequired]);

  const { trackPublisherPreLoginEvent, trackPublisherPostLoginEvent } = useTrackPublisherEvents();
  const storeSessionTokenInDB = async (sessionToken: string, id: string) => {
    try {
      await addValue.mutateAsync({
        tableName: 'sessions',
        value: {
          id: id,
          sessionToken: sessionToken,
        },
      });
    } catch (error) {
      console.error('Error storing session token in DB', error);
    }
  };

  const failedLoginHandler = async ({
    method,
    msg,
    enrichedCustomMsg,
    isUrlOtpAuth = false,
    isTempSession = false,
    shouldBlockStoreInCampaign = false,
    title,
    reasonCode = EOtpAuthReasonCode.LOGIN_REQUEST_FAILED,
  }: {
    method: string;
    msg?: string;
    enrichedCustomMsg?: string;
    isUrlOtpAuth?: boolean;
    isTempSession?: boolean;
    shouldBlockStoreInCampaign?: boolean;
    title?: string;
    reasonCode?: OtpAuthReasonCode;
  }) => {
    const reason = msg || enrichedCustomMsg || ELoginResultReason.UNKNOWN;

    loginStateService.setLoginError(reason);

    console.error('Login failed:', reason);

    // Recorded before the early return below, which ends the flow without a
    // redirect: that branch is the one that leaves no trace anywhere.
    logPortalRumError(EPortalRumEvents.PORTAL_LOGIN_FAILED, {
      playerId: webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA)?.playerId,
      method,
      reasonCode,
      isTempSession,
      pathname,
      appSessionId: webStorageUtil.getStorageWithFallback()?.id,
      journeyId: readLandingJourneyId(),
    });

    trackMixpanelEvent(
      EEventsType.PORTAL_LOGIN_COMPLETED,
      getLoginEventsData({
        method: loginPageService.mapLoginModeToLoginMethod[method as ELoginMode],
        platform,
        options: { result: EResultOptions.FAILED, reason },
      })
    );

    trackPublisherPostLoginEvent.mutate({
      ...enrichOptions({
        platform,
        eventName: EPublisherEvents.PORTAL_LOGIN_COMPLETED,
        method: method as ELoginMethods,
        result: EResultOptions.FAILED,
        reason,
        playerSession: { sessionMetadata: { source: getSessionMetadataSource(pathname) } },
      }),
    });

    if (method === ELoginMethods.OTP && !isUrlOtpAuth) {
      return;
    }

    const errorMessage = reason || 'Authentication failed';
    router.replace(
      `/failed?error=auth&message=${encodeURIComponent(errorMessage)}&authMethod=${method.toLowerCase()}${
        title ? `&title=${encodeURIComponent(title)}` : ''
      }`
    );

    // const loginFailureData = {
    //   result: EResultOptions.FAILED,
    //   reason
    // };
    // customEvents.sendCustomEvent(
    //   EEventsType.LOGIN_RESULT,
    //   {
    //     authMethod: method,
    //     ...loginFailureData,
    //     login_duration: calcLoginDuration(),
    //     platform: getPlatformData(),
    //     isTempSession
    //   },
    //   EStorePhase.PRE_LOGIN
    // );
    // await sendPublisherWebhookLoginEvent({
    //   eventName: ELoginEvents.LOGIN_RESOLVED,
    //   method: method as ELoginMethods,
    //   options: loginFailureData
    // });
    // await sendPublisherWebhookLoginEventV2({
    //   eventName: ELoginEventsV2.WEBSTORE_LOGIN_COMPLETED,
    //   eventType: EPublisherWebhookEventTypeV2.POST_LOGIN,
    //   method: method as ELoginMethods,
    //   options: loginFailureData
    // });
    // const queryParams = new URLSearchParams({
    //   error: 'auth',
    //   ...(msg && { msg })
    // });
    // router.replace(`/failed?msg=${reason}`);
  };

  const handleLoginError = async (
    error: any,
    authMethod: string,
    isUrlOtpAuth: boolean,
    reasonCode: OtpAuthReasonCode
  ) => {
    const defaultErrorMessage = 'Login Failed';
    const { enrichedMessage, code, message, title } = error.response?.data || {};
    const errorCode = error.response?.data?.code;

    // Provide specific user-friendly message for OTP validation errors
    let errorMessage: string | undefined;
    if (authMethod === ELoginMethods.OTP) {
      errorMessage = isUrlOtpAuth
        ? t('portal.failed.authenticationFailed')
        : t('portal.login.otpCodeIncorrect');
    } else {
      errorMessage = enrichedMessage
        ? undefined
        : code === 100
          ? message
          : errorCode >= 500 && errorCode <= 3000
            ? 'We are down for a bit of maintenance. We will be up and running again shortly!'
            : defaultErrorMessage;
    }

    await failedLoginHandler({
      method: authMethod,
      msg: errorMessage,
      enrichedCustomMsg: enrichedMessage,
      isUrlOtpAuth,
      title,
      reasonCode,
    });
  };

  const revokeExistingSession = () => {
    loginStateService.setIsLoggedIn(false);
    webStorageUtil.remove(ELocalStorageKeys.SESSION_TOKEN);
    webStorageUtil.remove(ELocalStorageKeys.PLAYER_DATA);
    deleteAll.mutate('sessions');
  };

  const failLoginJourneys = (
    journey: ReturnType<typeof createPortalJourney>,
    response: LoginData,
    reasonCode: OtpAuthReasonCode
  ): void => {
    journey.fail({ reasonCode });
    if (response.authMethod === ELoginMethods.OTP) {
      failPersistentPortalJourney(EPortalJourneyFlow.OTP_ROUND_TRIP, {
        pathname,
        reasonCode,
      });
    }
  };

  const loginResponse = async (
    response: LoginData,
    isUrlOtpAuth: boolean = false,
    serverLogin?: IServerOtpLogin
  ): Promise<boolean> => {
    const journey = createPortalJourney({
      flow: EPortalJourneyFlow.LOGIN,
      context: {
        authMethod: response.authMethod,
        pathname,
      },
    });
    let hasError = false;
    try {
      loginStateService.setIsLoggingIn(true);
      loginStateService.setLoginError(null);

      if (bootData?.featureFlags?.store_revoke_session_on_new_auth) {
        revokeExistingSession();
      }
      if (serverLogin) {
        adoptAppSession(serverLogin.sessionId);
      }

      const loginPayload = await prepareLoginPayload(response);
      const { data } = await performLogin(loginPayload, response.authMethod, serverLogin);
      journey.markStep('login-response', { source: serverLogin ? 'server' : 'app' });
      const loginSucceeded = await processSuccessfulLogin(
        response,
        data,
        false,
        isUrlOtpAuth,
        serverLogin?.eventEmitted
      );
      if (!loginSucceeded) {
        hasError = true;
        failLoginJourneys(
          journey,
          response,
          classifyOtpBusinessFailureReason(data.errorMessage)
        );
        return false;
      }

      journey.markStep('session-commit');
      journey.complete();
      if (response.authMethod === ELoginMethods.OTP) {
        completePersistentPortalJourney(
          EPortalJourneyFlow.OTP_ROUND_TRIP,
          { pathname }
        );
      }
      return true;
    } catch (error: any) {
      hasError = true;
      const reasonCode = classifyLoginFailureReason(error);
      failLoginJourneys(journey, response, reasonCode);
      await handleLoginError(error, response.authMethod, isUrlOtpAuth, reasonCode);
      return false;
    } finally {
      loginStateService.setIsLoggingIn(false);

      if (response.authMethod === ELoginMethods.OTP && (hasError || !response.otp?.accessToken)) {
        loginStateService.setIsOtpLoggingIn(false);
      }
      if (!hasError && !(response.authMethod === ELoginMethods.OTP && response.otp?.accessToken)) {
        loginStateService.setIsLoginModalOpen(false);
      }
    }
  };

  const prepareLoginPayload = async (response: LoginData) => {
    const currentInitialBoot = appStateService.initialBoot.get();
    const isProfilesEnabled = currentInitialBoot?.integrations?.isProfilesEnabled;
    const hasIntegrationFeatureFlag = bootData?.featureFlags?.integration_profiles;
    const profileName = webStorageUtil.get(ELocalStorageKeys.INTEGRATION_PORFILE);

    // Handle profile image URL for social logins
    if (response.authMethod === ELoginMethods.FACEBOOK) {
      try {
        response.profileImageUrl = await facebook.getFacebookProfile(response.userToken!);
      } catch (error) {
        console.error('Failed to load profile picture', error);
      }
    }
    if (response.authMethod === ELoginMethods.GOOGLE) {
      try {
        response.profileImageUrl = await google.getGoogleProfile(response.userToken!);
      } catch (error) {
        console.error('Failed to load Google profile picture', error);
      }
    }

    // Add profile to user token if needed
    if (isProfilesEnabled && hasIntegrationFeatureFlag && profileName) {
      response.userToken = `${response.userToken}::${profileName}`;
    }

    const sessionData = webStorageUtil.getStorageWithFallback();
    const loginData: {
      data: LoginRequestData;
    } = {
      data: {
        publisherId: currentInitialBoot?.stores[0] || '',
        localAddress: localAddress,
        sessionId: sessionData?.id ?? '',
        ...response,
      },
    };

    // // Add OS information if feature flag is enabled
    // if (bootData?.featureFlags?.store_send_os_authlogin) {
    //   loginData.data.os = isIOS ? EPlatformType.IOS : isAndroid ? EPlatformType.ANDROID : EPlatformType.WEB;
    // }

    if (response.authMethod === 'otp' && loginStateService.proofKey.get()) {
      loginStateService.setLoggingWithProofKey(true);
    }

    // Send login attempt event
    // customEvents.sendCustomEvent(
    //   EEventsType.LOGIN_ATTEMPTED,
    //   loginPageService.getLoginEventsData({
    //     eventSharedProps: customEvents.getEventsSharedProps(),
    //     method: response.authMethod
    //   }),
    //   EStorePhase.PRE_LOGIN
    // );

    // Prepare platform information
    const platformBase = getPlatformData().toUpperCase();
    const platform =
      isProfilesEnabled && hasIntegrationFeatureFlag && profileName
        ? `${platformBase}::${profileName}`
        : platformBase;

    return {
      ...loginData.data,
      platform,
      isIntegrationProfilesEnabled: isProfilesEnabled,
    };
  };

  /**
   * The app session the server opened when it logged the player in: the login
   * and its event already carry it, so every event from here on has to as well.
   */
  const adoptAppSession = (sessionId: string): void => {
    const session = { id: sessionId, ttl: Date.now() + DEFAULT_TTL };
    webStorageUtil.set(ESessionStorageKeys.SESSION_DATA, session, EStorageType.SESSION_STORAGE);
    webStorageUtil.set(ELocalStorageKeys.SESSION_DATA, session, EStorageType.LOCAL_STORAGE);
  };

  /**
   * The login response, from the server's exchange when the document carried
   * one — the credentials are one-time, so they are never sent twice — and
   * from a request of our own otherwise.
   */
  const performLogin = async (
    loginPayload: any,
    authMethod?: ELoginMethods,
    serverLogin?: IServerOtpLogin
  ): Promise<any> => {
    if (serverLogin?.response) {
      return { data: serverLogin.response };
    }
    if (serverLogin?.rejection) {
      throw toLoginRequestError(serverLogin.rejection);
    }

    try {
      return await login.mutateAsync(loginPayload);
    } catch (error) {
      if (
        authMethod === ELoginMethods.OTP &&
        isTransportLoginFailure(classifyLoginFailureReason(error))
      ) {
        return await login.mutateAsync(loginPayload);
      }
      throw error;
    }
  };

  const processSuccessfulLogin = async (
    response: LoginData,
    data: LoginResponse,
    isTempSession = false,
    isUrlOtpAuth = false,
    isPublisherEventEmitted = false
  ): Promise<boolean> => {
    // const loginDuration = calcLoginDuration();

    if (data.errorCode === 100) {
      let reasonCode: OtpAuthReasonCode = EOtpAuthReasonCode.LOGIN_REQUEST_FAILED;
      if (response.authMethod === ELoginMethods.OTP) {
        reasonCode = classifyOtpBusinessFailureReason(data.errorMessage);
      }

      await failedLoginHandler({
        method: response.authMethod,
        isUrlOtpAuth,
        msg:
          response.authMethod === ELoginMethods.OTP && isUrlOtpAuth
            ? t('portal.failed.authenticationFailed')
            : data.errorMessage,
        reasonCode,
      });
      return false;
    }

    trackMixpanelEvent(
      EEventsType.PORTAL_LOGIN_COMPLETED,
      getLoginEventsData({
        method: response.authMethod,
        platform,
        options: {
          result: EResultOptions.SUCCESS,
          playerId: data.playerId,
          playerName: data.playerName,
        },
      })
    );

    // Skipped when the server already sent it with the login: one event per login.
    if (!isPublisherEventEmitted) {
      trackPublisherPostLoginEvent.mutate({
        ...enrichOptions({
          platform,
          eventName: EPublisherEvents.PORTAL_LOGIN_COMPLETED,
          result: EResultOptions.SUCCESS,
          method: response.authMethod,
          playerSession: { sessionMetadata: { source: getSessionMetadataSource(pathname) } },
          customer: {
            playerId: data.playerId,
            playerName: data.playerName,
          },
        }),
      });
    }

    if (data.sessionToken) {
      webStorageUtil.set(ELocalStorageKeys.SESSION_TOKEN, data.sessionToken);
    }

    const previousPlayer = webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA);
    const isDifferentPlayer =
      !previousPlayer?.playerId || previousPlayer.playerId !== data.playerId;
    if (isDifferentPlayer) {
      webStorageUtil.remove(ELocalStorageKeys.PLAYER_DATA);
      popupStateService.clear();
      dailyBonusStateService.clear();
      broadcastRegistry.resetCounter(STORE_REFRESH_CHANNEL_NAME);
      queryClient.removeQueries({ queryKey: [EQueryKeys.OFFERS] });
      queryClient.removeQueries({ queryKey: [EQueryKeys.PAGES] });
    }

    webStorageUtil.set(ELocalStorageKeys.PLAYER_DATA, {
      playerId: data.playerId,
      playerName: data.playerName,
      playerCountry: data.playerCountry,
      profileImageUrl: data.profileImageUrl || data.playerProfileImage,
      authMethod: response.authMethod,
    });

    setPortalPlayerRumContext({
      playerId: data.playerId,
      playerName: data.playerName,
      playerCountry: data.playerCountry,
      authMethod: response.authMethod,
      isTempSession,
    });
    logPortalRumInfo(EEventsType.PORTAL_LOGIN_COMPLETED, {
      playerId: data.playerId,
      authMethod: response.authMethod,
      isTempSession,
    });

    loginStateService.setIsLoggedIn(true);

    void persistSessionAndCampaignRedirect(response, data);

    return true;
  };

  const releaseOtpLoginUi = (): void => {
    loginStateService.setIsOtpRedirectPending(false);
    loginStateService.setIsOtpLoggingIn(false);
    loginStateService.setIsLoginModalOpen(false);
    clearOtpUrlAuth();
  };

  const shouldDispatchCampaignRedirect = (sessionToken: string | undefined): boolean => {
    if (!loginStateService.isLoggedIn.get()) {
      return false;
    }
    if (sessionToken === undefined || sessionToken === '') {
      return false;
    }
    return webStorageUtil.get(ELocalStorageKeys.SESSION_TOKEN) === sessionToken;
  };

  const persistSessionAndCampaignRedirect = async (
    response: LoginData,
    data: LoginResponse
  ): Promise<void> => {
    if (data.sessionToken) {
      await storeSessionTokenInDB(data.sessionToken, data.playerId);
    }

    if (response.authMethod !== ELoginMethods.OTP || !response.otp?.accessToken) {
      return;
    }

    loginStateService.setIsOtpRedirectPending(true);

    const dispatchCampaignRedirect = (redirectPath: string): void => {
      if (shouldDispatchCampaignRedirect(data.sessionToken)) {
        router.replace(redirectPath);
      }
      releaseOtpLoginUi();
    };

    try {
      const metadataResponse = await axios.get(
        `${getEnvVariable('NEXT_PUBLIC_REACT_APP_API_BASE_URL', process.env.NEXT_PUBLIC_REACT_APP_API_BASE_URL) || 'https://api-store-dev.appcharge.com'}/store/campaign/metadata?token=${encodeURIComponent(response.otp.accessToken)}`,
        {
          headers: {
            Authorization: `Bearer ${data.sessionToken}`,
          },
        }
      );

      if (metadataResponse?.data?.loginPageSource) {
        const pageName = metadataResponse.data.loginPageSource;
        let redirectPath = '/';

        const currentBoot = appStateService.initialBoot.get();

        if (currentBoot?.pages) {
          const pageEntry = Object.entries(currentBoot.pages).find(
            ([, page]) => page.pageName === pageName
          );
          if (pageEntry) {
            const slug = pageEntry[1].slug;
            const leaf = getLeafSlug(slug);
            redirectPath = leaf === 'home' ? '/' : `/${leaf}`;
          }
        }

        dispatchCampaignRedirect(redirectPath);
        return;
      }

      dispatchCampaignRedirect('/');
    } catch {
      releaseOtpLoginUi();
    }
  };

  //   const navigateToMode = (mode: ELoginMode) => {
  //     return mode !== 'providers' ? navigate(`./${mode}`) : navigate(-1);
  //   };

  const loginButtonClickWrapper = async (
    callback: () => Promise<void> | void,
    method: ELoginMethods
  ) => {
    if (
      method === ELoginMethods.FACEBOOK ||
      method === ELoginMethods.GOOGLE ||
      method === ELoginMethods.APPLE
    ) {
      if (method === ELoginMethods.FACEBOOK) {
        webStorageUtil.set(ELocalStorageKeys.LAST_VISITING_PAGE, pathname);
      }
      //   recordLoginStartTime();
      loginStateService.setIsLoggingIn(true);

      trackMixpanelEvent(
        EEventsType.PORTAL_LOGIN_METHOD_SELECTED,
        getLoginEventsData({ platform, method: method })
      );
      trackPublisherPreLoginEvent.mutate({
        ...enrichOptions({
          platform,
          eventName: EPublisherEvents.PORTAL_LOGIN_METHOD_SELECTED,
          method: method,
          playerSession: { source: getSessionMetadataSource(pathname) },
        }),
      });
    }
    webStorageUtil.set(ESessionStorageKeys.REDIRECT_STATE, `${method}redirect`);
    callback();
  };

  const handleBackBtnClicked = async () => {
    const mode = loginStateService.mode.get() as ELoginMode;

    trackMixpanelEvent(
      EEventsType.PORTAL_LOGIN_CANCELED,
      getLoginEventsData({ method: loginPageService.mapLoginModeToLoginMethod[mode], platform })
    );

    trackPublisherPreLoginEvent.mutate({
      ...enrichOptions({
        platform,
        eventName: EEventsType.PORTAL_LOGIN_CANCELED,
        method: mode,
        playerSession: { source: getSessionMetadataSource(pathname) },
      }),
    });
    // navigateToMode(ELoginMode.PROVIDERS); // todo ?????????/
    // customEvents.sendCustomEvent(
    //   EEventsType.LOGIN_CANCELED,
    //   loginPageService.getLoginEventsData({
    //     eventSharedProps: customEvents.getEventsSharedProps()
    //   }),
    //   EStorePhase.PRE_LOGIN
    // );
    // sendPublisherWebhookLoginEvent({
    //   eventName: ELoginEvents.LOGIN_CANCELED,
    //   method: loginPageService.mapLoginModeToLoginMethod[mode]
    // });
    // sendPublisherWebhookLoginEventV2({
    //   eventName: ELoginEventsV2.WEBSTORE_LOGIN_CANCELED,
    //   eventType: EPublisherWebhookEventTypeV2.PRE_LOGIN,
    //   method: loginPageService.mapLoginModeToLoginMethod[mode]
    // });
  };

  const resetAppState = (utmSource?: string) => {
    resetMixpanelSession();
    setDisplayLoader(false);
    loginStateService.setIsLoggingIn(false);
    loginStateService.setIsLoginModalOpen(false);
    loginStateService.setLoginType(null);

    queryClient.removeQueries({ queryKey: ['offers'] });
    queryClient.removeQueries({ queryKey: [EQueryKeys.PAGES] });

    const loggedOutPages = initialBoot?.pages;
    webStorageUtil.clear();

    const newSession = {
      id: uuidv4(),
      ttl: Date.now() + DEFAULT_TTL,
    };
    webStorageUtil.set(ESessionStorageKeys.SESSION_DATA, newSession, EStorageType.SESSION_STORAGE);
    webStorageUtil.set(ELocalStorageKeys.SESSION_DATA, newSession, EStorageType.LOCAL_STORAGE);
    webStorageUtil.remove(ESessionStorageKeys.REDIRECT_STATE);

    const redirectPath = getRedirectPath(loggedOutPages);
    router.replace(redirectPath);
  };

  const logout = async (utmSource?: string) => {
    loginStateService.setIsLoggedIn(false);
    resetAppState(utmSource);
    deleteAll.mutate('sessions');
  };

  return {
    showCookiesOverlay: useObservable(loginStateService.showCookiesOverlay),
    proofKey: useObservable(loginStateService.proofKey),
    loggingWithProofKey: useObservable(loginStateService.loggingWithProofKey),
    otpToken: useObservable(loginStateService.otpToken),
    mode: useObservable(loginStateService.mode),
    hasConsent: useObservable(loginStateService.hasConsent),
    shouldShowTermsAlert: useObservable(loginStateService.shouldShowTermsAlert),
    isTermsAlertShaking: useObservable(loginStateService.isTermsAlertShaking),
    isLoginModalOpen: useObservable(loginStateService.isLoginModalOpen),
    isLoggingIn: useObservable(loginStateService.isLoggingIn),
    loginType: useObservable(loginStateService.loginType),
    isLoggedIn: Boolean(useObservable(loginStateService.isLoggedIn)),
    isOtpRedirectPending: useObservable(loginStateService.isOtpRedirectPending),
    isOtpLoggingIn,
    loginError: useObservable(loginStateService.loginError),
    isConsentChecked,
    shouldDisableLoginButtons,
    setShowCookiesOverlay: loginStateService.setShowCookiesOverlay,
    setProofKey: loginStateService.setProofKey,
    setLoggingWithProofKey: loginStateService.setLoggingWithProofKey,
    setOtpToken: loginStateService.setOtpToken,
    setMode: loginStateService.setMode,
    setHasConsent: loginStateService.setHasConsent,
    setShouldShowTermsAlert: loginStateService.setShouldShowTermsAlert,
    setIsTermsAlertShaking: loginStateService.setIsTermsAlertShaking,
    setIsLoginModalOpen: loginStateService.setIsLoginModalOpen,
    setIsLoggingIn: loginStateService.setIsLoggingIn,
    setLoginType: loginStateService.setLoginType,
    setIsLoggedIn: loginStateService.setIsLoggedIn,
    setIsOtpRedirectPending: loginStateService.setIsOtpRedirectPending,
    setIsOtpLoggingIn: loginStateService.setIsOtpLoggingIn,
    setIsConsentChecked: loginStateService.setIsConsentChecked,
    setLoginError: loginStateService.setLoginError,
    loginResponse,
    processSuccessfulLogin,
    failedLoginHandler,
    loginButtonClickWrapper,
    handleBackBtnClicked,
    logout,
    clear: loginStateService.clear,
  };
};
